import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import { BOARD_BASIS_INCLUDED_LABELS, pickPerPersonPerNightLabel, pickOccupancyBasisLabel } from "@/lib/pricingDefaults";

// Smooth-expand drawer revealing the four board-plan prices for a single room.
// Used both inside the spotlight room cards (RoomServicePricing) and inside
// the editorial Room Rates strip on each hotel detail page.
//
// Values shown are ALWAYS per person and night — pass the per-person
// plans in via `plans`. Optional `occupancy` renders a caption stating
// which occupancy the per-person figure is calculated against.

const TOGGLE_COPY = {
  show: { en: "View board options", fr: "Voir les options de pension", es: "Ver opciones de pensión" },
  hide: { en: "Hide board options", fr: "Masquer les options",         es: "Ocultar opciones" },
};
const BOARD_ORDER = ["room_only", "breakfast", "half_board", "full_board"];
// Map internal plan key → BOARD_BASIS_INCLUDED_LABELS canonical key.
const BOARD_ALIAS = {
  room_only: "room_only",
  breakfast: "bb",
  half_board: "hb",
  full_board: "fb",
};

const pick = (obj, lang) => (typeof obj === "string" ? obj : obj?.[lang] || obj?.en || "");

// `variant` switches the colour palette so the same drawer reads correctly
// against a light card (default) and against the dark Room Rates strip (dark).
const BoardOptionsDropdown = ({ plans, lang, testid, variant = "light", occupancy }) => {
  const { fromMAD } = useFormatPrice();
  const available = useMemo(
    () => BOARD_ORDER.filter((k) => plans?.[k] != null && !Number.isNaN(Number(plans[k]))),
    [plans]
  );
  const cheapest = useMemo(() => {
    if (!available.length) return null;
    return [...available].sort((a, b) => Number(plans[a]) - Number(plans[b]))[0];
  }, [available, plans]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(cheapest);

  if (!available.length) return null;

  const isDark = variant === "dark";
  const toggleCls = isDark
    ? "border-[#FDFBF7]/20 hover:border-[#D4A373] bg-[#1A1513]/40 hover:bg-[#1A1513]/60 text-[#FDFBF7]"
    : "border-[#2C2621]/15 hover:border-[#D4A373] bg-[#FDFBF7] hover:bg-[#F2EBE1]/50 text-[#2C2621]";
  const listCls = isDark
    ? "border-[#FDFBF7]/15 divide-[#FDFBF7]/10 bg-[#1A1513]/40"
    : "border-[#2C2621]/10 divide-[#2C2621]/10 bg-[#FDFBF7]";
  const rowSelCls = isDark
    ? "bg-[#D4A373]/20 border-l-[#D4A373]"
    : "bg-[#D4A373]/15 border-l-[#C16542]";
  const rowIdleCls = isDark
    ? "hover:bg-[#FDFBF7]/5 border-l-transparent"
    : "hover:bg-[#F2EBE1]/60 border-l-transparent";
  const labelSelCls = isDark ? "text-[#FDFBF7] font-medium" : "text-[#2C2621] font-medium";
  const labelIdleCls = isDark ? "text-[#FDFBF7]/75" : "text-[#5C5248]";
  const priceSelCls = isDark ? "text-[#D4A373]" : "text-[#C16542]";
  const priceIdleCls = isDark ? "text-[#FDFBF7]" : "text-[#2C2621]";
  const unitCls = isDark ? "text-[#FDFBF7]/60" : "text-[#5C5248]";
  const iconCls = isDark ? "text-[#D4A373]" : "text-[#A07042]";
  const captionCls = isDark ? "text-[#FDFBF7]/55" : "text-[#5C5248]";

  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${testid}-list`}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3 border transition-colors duration-200 text-left ${toggleCls}`}
        data-testid={`${testid}-toggle`}
      >
        <span className="text-[11px] tracking-[0.25em] uppercase">
          {pick(open ? TOGGLE_COPY.hide : TOGGLE_COPY.show, lang)}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-300 ${iconCls} ${open ? "rotate-180" : ""}`}
          strokeWidth={1.8}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr] mt-2" : "grid-rows-[0fr] mt-0"
        }`}
      >
        <div className="overflow-hidden">
          {occupancy ? (
            <p
              className={`text-[10px] tracking-[0.2em] uppercase mb-1.5 px-1 ${captionCls}`}
              data-testid={`${testid}-occupancy`}
            >
              {pickOccupancyBasisLabel(occupancy, lang)}
            </p>
          ) : null}
          <ul
            id={`${testid}-list`}
            role="listbox"
            aria-label={pick(TOGGLE_COPY.show, lang)}
            className={`border divide-y ${listCls}`}
            data-testid={`${testid}-list-ul`}
          >
            {available.map((key) => {
              const isSel = key === selected;
              const canonicalKey = BOARD_ALIAS[key] || "room_only";
              return (
                <li key={key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    onClick={() => setSelected(key)}
                    className={`w-full flex items-center justify-between gap-4 px-4 py-3 text-left transition-colors duration-200 border-l-2 ${
                      isSel ? rowSelCls : rowIdleCls
                    }`}
                    data-testid={`${testid}-option-${key}`}
                  >
                    <span className={`text-[11px] tracking-[0.2em] uppercase ${isSel ? labelSelCls : labelIdleCls}`}>
                      {pick(BOARD_BASIS_INCLUDED_LABELS[canonicalKey], lang)}
                    </span>
                    <span className="text-right whitespace-nowrap leading-none">
                      <span className={`font-serif-x text-base md:text-lg tracking-tight ${isSel ? priceSelCls : priceIdleCls}`}>
                        {fromMAD(plans[key])}
                      </span>
                      <span className={`ml-1.5 text-[10px] tracking-wide ${unitCls}`}>
                        / {pickPerPersonPerNightLabel(lang).split(" ")[0]}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BoardOptionsDropdown;
